import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useVideoPlayer } from '../hooks/VideoPlayerContext';

interface SpeakerSegment {
  startTime: number;
  endTime: number;
  speakerColor: string;
}

interface TranscriptWaveformProps {
  waveformData: number[];
  duration: number;
  speakerSegments?: SpeakerSegment[]; // Speaker segments with time ranges and colors
}

/**
 * Component for displaying audio waveform with current position
 */
export const TranscriptWaveform: React.FC<TranscriptWaveformProps> = ({
  waveformData,
  duration,
  speakerSegments = []
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localCurrentTime, setLocalCurrentTime] = useState(0);

  // Access the video player context
  const videoPlayerAPI = useVideoPlayer();

  // Set up a ref to track if we're currently updating from the video player
  // This helps prevent circular updates between waveform and video
  const updatingFromVideo = useRef(false);

  // A ref to track the last container width to detect resizing
  const lastContainerWidth = useRef(0);

  // Track window resizes to redraw the waveform with proper bar widths
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.getBoundingClientRect().width;
        if (newWidth !== lastContainerWidth.current) {
          lastContainerWidth.current = newWidth;
          // Redraw waveform by triggering a re-render
          setLocalCurrentTime(prev => prev); // Force re-render by updating state
        }
      }
    };

    // Initialize the container width
    if (containerRef.current) {
      lastContainerWidth.current = containerRef.current.getBoundingClientRect().width;
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Set up a timer to periodically synchronize the transcript time with the video player time
  useEffect(() => {
    let rafId: number | undefined;

    const sync = () => {
      // Avoid feedback loops if *we* just set the video time
      if (!updatingFromVideo.current) {
        const videoTime = videoPlayerAPI.getCurrentTime();

        // Skip NaN during player init
        if (!Number.isNaN(videoTime)) {
          updatingFromVideo.current = true;
          setLocalCurrentTime(videoTime);
          updatingFromVideo.current = false;
        }
      }

      // Schedule next frame
      rafId = requestAnimationFrame(sync);
    };

    rafId = requestAnimationFrame(sync);

    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, [videoPlayerAPI]);

  // Helper function to get speaker color at a specific time
  const getSpeakerColorAtTime = (time: number): string => {
    if (!speakerSegments || speakerSegments.length === 0) {
      return '#A9A9A9'; // Default color if no segments
    }

    // Find the segment that contains this time
    const segment = speakerSegments.find(
      segment => time >= segment.startTime && time <= segment.endTime
    );

    return segment ? segment.speakerColor : '#A9A9A9';
  };

  
  // Draw the waveform on the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || waveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions to match container (for high DPI displays)
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate how many bars we can fit with 3px width per bar (considering device pixel ratio)
    const targetBarWidthInPixels = 3 * window.devicePixelRatio;
    const targetBarGapInPixels = 1 * window.devicePixelRatio;
    const totalBarWidthWithGap = targetBarWidthInPixels + targetBarGapInPixels;
    const maxNumBars = Math.floor(canvas.width / totalBarWidthWithGap);

    // Calculate step size to display evenly distributed bars
    const waveStep = Math.max(1, Math.ceil(waveformData.length / maxNumBars));

    // Ensure we don't exceed the data array bounds
    const numBars = Math.min(maxNumBars, Math.floor(waveformData.length / waveStep));

    let bars = [];
    let maxAmpHeight = 0;
    let maxRmsHeight = 0;
    let minAmpHeight = 255;
    let minRmsHeight = 255;
    // Draw waveform bars with fixed 3px width
    for (let i = 0; i < numBars; i++) {
      const startIndex = i * waveStep;
      const endIndex = Math.min(startIndex + waveStep, waveformData.length);

      if (startIndex >= waveformData.length) break;

      // Find maximum and RMS amplitude in this segment
      let maxAmplitude = 0;
      let sumSquares = 0;
      for (let j = startIndex; j < endIndex; j++) {
        maxAmplitude = Math.max(maxAmplitude, waveformData[j]);
        sumSquares += Math.pow(waveformData[j], 2);
      }

      // Calculate RMS (root mean square) - a better energy representation
      const count = endIndex - startIndex;
      const rmsAmplitude = count > 0 ? Math.sqrt(sumSquares / count) : 0;

      // Calculate the time corresponding to this position in the waveform
      const time = (startIndex / waveformData.length) * duration;

      // Get the color for this position based on speaker segments
      const baseColor = getSpeakerColorAtTime(time);

      if (maxAmplitude > maxAmpHeight) {
        maxAmpHeight = maxAmplitude;
      }

      if (rmsAmplitude > maxRmsHeight) {
        maxRmsHeight = rmsAmplitude;
      }

      // extra metric condition to avoid false positives
      if (maxRmsHeight > 0 && maxAmplitude > 0 && maxAmplitude < minAmpHeight && maxAmplitude > maxRmsHeight * 1.5) {
        minAmpHeight = maxAmplitude;
      }

      if (rmsAmplitude > 0 && rmsAmplitude < minRmsHeight) {
        minRmsHeight = rmsAmplitude;
      }

      bars.push({
        color: baseColor,
        ampHeight: maxAmplitude,
        rmsHeight: rmsAmplitude,
      })
    }

    const ampHeightRange = maxAmpHeight - minAmpHeight;
    const rmsHeightRange = maxRmsHeight - minRmsHeight;


    for (let i = 0; i < bars.length; i++) {
      const bar = bars[i];
      const { color, ampHeight, rmsHeight } = bar;

      // Create a darker version for the RMS part
      ctx.fillStyle = color;

      // Process the max value (peaks)
      const rawMaxValue = (ampHeight - minAmpHeight) / ampHeightRange;
      // Process the RMS value (average energy)
      const rawRmsValue = (rmsHeight - minRmsHeight) / rmsHeightRange;

      // Scale values to canvas height
      const maxDrawHeight = rawMaxValue * canvas.height * 0.99;
      const rmsDrawHeight = rawRmsValue * canvas.height * 0.45; // RMS is smaller
      // Position on canvas
      const x = i * totalBarWidthWithGap;

      // Draw the maximum value bar (full width)
      const yMax = canvas.height - maxDrawHeight;
      ctx.fillRect(x, yMax, targetBarWidthInPixels, maxDrawHeight);

      // Draw the RMS value with slightly darker color
      // Manually darken the color by creating a semi-transparent overlay
      ctx.fillStyle = "#333333";
      ctx.globalAlpha = 0.3; // Make it slightly more transparent for a darker appearance
      const yRms = canvas.height - rmsDrawHeight;
      ctx.fillRect(x, yRms, targetBarWidthInPixels, rmsDrawHeight);
      ctx.globalAlpha = 1.0; // Reset transparency
    }

    // Draw current position marker
    const position = (localCurrentTime / duration) * canvas.width;

    // Draw full height indicator line
    ctx.fillStyle = '#FFFFFF'; // White playhead
    ctx.fillRect(position - 1.5 * window.devicePixelRatio, 0, 3 * window.devicePixelRatio, canvas.height);

    // Add glow effect to playhead
    ctx.shadowColor = '#F2D093';
    ctx.shadowBlur = 5 * window.devicePixelRatio;
    ctx.fillStyle = '#F2D093';
    ctx.fillRect(position - 1 * window.devicePixelRatio, 0, 2 * window.devicePixelRatio, canvas.height);

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

  }, [waveformData, localCurrentTime, duration, speakerSegments]);
  
  // Calculate position and time based on mouse position
  const calculateTimeFromMousePosition = (e: React.MouseEvent<HTMLCanvasElement>): number => {
    if (!canvasRef.current) return localCurrentTime;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const time = position * duration;
    
    return Math.max(0, Math.min(time, duration));
  };

  // Handle mouse interactions
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    // Directly seek when clicked, even without dragging
    const time = calculateTimeFromMousePosition(e);
    handleTimeChange(time);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging) {
      const time = calculateTimeFromMousePosition(e);
      handleTimeChange(time);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };
  
  // Custom handler for time changes in the waveform
  const handleTimeChange = (time: number) => {
    // Update local state
    setLocalCurrentTime(time);
    
    // Also update video player if available
    if (videoPlayerAPI && !updatingFromVideo.current) {
      updatingFromVideo.current = true;
      videoPlayerAPI.seekTo(time);
      setTimeout(() => {
        updatingFromVideo.current = false;
      }, 50);
    }
  };

  return (
    <Container ref={containerRef}>
      <TimeDisplay>
        <CurrentTime>{formatTime(localCurrentTime)}</CurrentTime>
        <TotalTime>{formatTime(duration)}</TotalTime>
      </TimeDisplay>
      <Canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        title="Click to seek to this position in the audio/video"
      />
    </Container>
  );
};

// Format time display (MM:SS)
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Styled components
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #161725; /* Darker background for better contrast */
  border-radius: 8px 0 0 0;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.3); /* Inner shadow for depth */
  border: 1px solid #2a2a3d; /* Subtle border */
`;

const Canvas = styled.canvas`
  width: 100%;
  height: calc(100% - 24px);
  cursor: pointer;
  margin-top: 4px;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #A9A9A9;
  padding: 10px;
`;

const CurrentTime = styled.span`
  color: #f2d093;
  font-weight: 500;
`;

const PlaybackHint = styled.span`
  color: #5f5f5f;
  font-size: 10px;
  font-style: italic;
`;

const TotalTime = styled.span`
  color: #888;
`;